import { Text } from 'react-native';
import { useRouter, Href } from 'expo-router';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { OnboardingStep } from '@/lib/state/inverio-store';
import AnimatedPressable from '@/components/AnimatedPressable';

interface SkipButtonProps {
  step: OnboardingStep;
  nextRoute: Href;
  label?: string;
  delay?: number;
}

/**
 * A skip button for optional onboarding steps.
 * Records the step as skipped in the store before navigating.
 */
export function SkipButton({
  step,
  nextRoute,
  label = 'Skip for now',
  delay = 400,
}: SkipButtonProps) {
  const colors = useSystemThemeColors();
  const router = useRouter();
  const skipOnboardingStep = useProfileStore((s) => s.skipOnboardingStep);
  const isStepSkippable = useProfileStore((s) => s.isStepSkippable);

  // Don't render if step is not skippable
  if (!isStepSkippable(step)) {
    return null;
  }

  const handleSkip = () => {
    skipOnboardingStep(step);
    router.push(nextRoute);
  };

  return (
    <Animated.View
      entering={FadeInUp.delay(delay).duration(500)}
      className="px-5 pb-8"
    >
      <AnimatedPressable
        onPress={handleSkip}
        className="py-3 items-center"
      >
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-sm font-medium"
        >
          {label}
        </Text>
      </AnimatedPressable>
    </Animated.View>
  );
}
